import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navbar, Container, Nav, Dropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import LoginModal from './LoginModal';
import { FaUserCircle } from 'react-icons/fa';
import {
  FiExternalLink,
  FiMail,
  FiX,
  FiCheck,
  FiBookOpen,
  FiLayers,
  FiTerminal,
} from 'react-icons/fi';
import { SHOW_CURRICULUM_SECTION } from '../siteConfig';

const DESKTOP_DEV_HIDE_DELAY_MS = 600;
const TOUCH_MOUSE_SUPPRESSION_MS = 800;

const StyledNavbar = styled(Navbar)`
  transition: all 0.3s ease-out;
  background: ${({ $variant, $scrolled }) => (
    $variant === 'light'
      ? ($scrolled ? 'rgba(252, 250, 246, 0.94)' : 'rgba(252, 250, 246, 0.86)')
      : ($scrolled ? 'var(--glass-bg)' : 'transparent')
  )};
  backdrop-filter: ${({ $variant, $scrolled }) => (
    $variant === 'light' ? 'blur(18px)' : ($scrolled ? 'blur(10px)' : 'none')
  )};
  -webkit-backdrop-filter: ${({ $variant, $scrolled }) => (
    $variant === 'light' ? 'blur(18px)' : ($scrolled ? 'blur(10px)' : 'none')
  )};
  border-bottom: ${({ $variant, $scrolled }) => (
    $variant === 'light'
      ? '1px solid rgba(20, 24, 31, 0.08)'
      : ($scrolled ? 'var(--glass-border)' : 'none')
  )};
  box-shadow: ${({ $variant, $scrolled }) => (
    $variant === 'light' && $scrolled
      ? '0 10px 40px rgba(50, 45, 36, 0.07)'
      : 'none'
  )};
  z-index: ${({ $menuOpen }) => ($menuOpen ? 5000 : 2000)};

  .navbar-brand, .nav-link {
    color: ${({ $variant }) => ($variant === 'light' ? '#171a20' : 'var(--text-color)')} !important;
    font-weight: 600;
    transition: color 0.2s ease;
    text-shadow: ${({ $variant }) => ($variant === 'light' ? 'none' : '0 1px 3px rgba(0,0,0,0.2)')};
  }

  .nav-link:hover {
    color: ${({ $variant }) => ($variant === 'light' ? '#6457d9' : 'var(--accent-color-1)')} !important;
  }

  .terminal-access-btn {
    position: relative;
    background: rgba(255, 255, 255, 0.05) !important;
    color: rgba(255, 255, 255, 0.9) !important;
    padding: 0.45rem 1.25rem !important;
    border-radius: 12px !important;
    font-weight: 700 !important;
    margin-left: 0.8rem !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    font-size: 0.85rem !important;
    letter-spacing: -0.01em !important;
    display: flex !important;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;

    svg {
        transition: all 0.3s ease;
        color: rgba(255, 255, 255, 0.6);
        margin-bottom: 1px;
    }

    &:hover {
        transform: translateY(-1px);
        background: rgba(255, 255, 255, 0.1) !important;
        border-color: rgba(255, 255, 255, 0.5) !important;
        color: #fff !important;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        
        svg {
            color: #fff;
        }
    }

    &.inquiry-btn {
      border-color: rgba(160, 120, 255, 0.4) !important;
      background: rgba(160, 120, 255, 0.06) !important;
      color: rgba(200, 180, 255, 1) !important;
      svg { color: rgba(160, 120, 255, 0.8); }
      
      &:hover {
        border-color: rgba(160, 120, 255, 0.8) !important;
        background: rgba(160, 120, 255, 0.12) !important;
        box-shadow: 0 4px 15px rgba(160, 120, 255, 0.2);
        color: #fff !important;
        svg { color: #fff; }
      }
    }

    &.service-btn {
      border-color: rgba(0, 210, 255, 0.4) !important;
      background: rgba(0, 210, 255, 0.06) !important;
      color: rgba(150, 230, 255, 1) !important;
      svg { color: rgba(0, 210, 255, 0.8); }

      &:hover {
        border-color: rgba(0, 210, 255, 0.8) !important;
        background: rgba(0, 210, 255, 0.12) !important;
        box-shadow: 0 4px 15px rgba(0, 210, 255, 0.2);
        color: #fff !important;
        svg { color: #fff; }
      }
    }

    &.dev-service-btn {
      position: relative !important;
      margin-left: 0 !important;
      width: 100%;
      justify-content: center;
      border-color: rgba(34, 197, 94, 0.55) !important;
      background: rgba(18, 65, 45, 0.96) !important;
      color: #c9f7dc !important;
      box-shadow: 0 14px 32px rgba(0, 0, 0, 0.34), 0 0 18px rgba(34, 197, 94, 0.14);

      svg {
        color: #6ee7a0;
      }

      &:hover,
      &:focus-visible {
        border-color: rgba(74, 222, 128, 0.82) !important;
        background: rgba(20, 83, 55, 0.98) !important;
        color: #fff !important;
        box-shadow: 0 16px 34px rgba(0, 0, 0, 0.38), 0 0 22px rgba(34, 197, 94, 0.2);
      }
    }

  }

  .service-access-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .service-primary-row {
    display: contents;
  }

  .mobile-dev-menu-toggle {
    display: none;
  }

  .dev-service-actions {
    position: absolute;
    top: calc(100% + 0.55rem);
    right: 0;
    z-index: 2200;
    display: grid;
    width: max-content;
    min-width: 100%;
    gap: 0.42rem;
    animation: devPortLauncherIn 0.18s ease-out;
  }

  .port-access-wrap {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    margin-left: 0;
  }

  .terminal-access-btn.port-access-trigger {
    width: 100% !important;
    min-width: 7.25rem;
    justify-content: center;
    margin-left: 0 !important;
    padding-inline: 0.9rem !important;
    border-color: rgba(103, 232, 249, 0.28) !important;
    background: rgba(14, 53, 69, 0.96) !important;
    color: #cffafe !important;
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.32), 0 0 18px rgba(34, 211, 238, 0.12);

    svg {
      color: #67e8f9;
    }

    &[aria-expanded='true'] {
      border-color: rgba(103, 232, 249, 0.58) !important;
      background: rgba(17, 70, 91, 0.98) !important;
      color: #fff !important;
    }
  }

  .dev-port-launcher {
    position: absolute;
    top: calc(100% + 0.6rem);
    right: 0;
    z-index: 2200;
    width: min(21rem, calc(100vw - 2rem));
    padding: 0.85rem;
    border: 1px solid rgba(148, 163, 184, 0.24);
    border-radius: 16px;
    background:
      radial-gradient(circle at 85% 0%, rgba(34, 211, 238, 0.14), transparent 42%),
      rgba(10, 15, 25, 0.96);
    box-shadow:
      0 20px 50px rgba(0, 0, 0, 0.36),
      0 1px 0 rgba(255, 255, 255, 0.08) inset;
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    animation: devPortLauncherIn 0.2s ease-out;
  }

  .dev-port-launcher-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.65rem;
    color: #f8fafc;
    font-size: 0.76rem;
    font-weight: 760;
    letter-spacing: -0.015em;

  }

  .dev-port-heading-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
  }

  .dev-port-kicker {
    color: #67e8f9;
    font-size: 0.58rem;
    font-weight: 850;
    letter-spacing: 0.12em;
  }

  .dev-port-close {
    display: inline-flex;
    width: 1.7rem;
    height: 1.7rem;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.055);
    color: #cbd5e1;
    transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

    &:hover,
    &:focus-visible {
      transform: scale(1.04);
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
      outline: 2px solid rgba(103, 232, 249, 0.18);
      outline-offset: 1px;
    }
  }

  .dev-port-launcher-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.45rem;
  }

  .dev-port-input-shell {
    display: flex;
    min-width: 0;
    min-height: 2.65rem;
    align-items: center;
    border: 1px solid rgba(148, 163, 184, 0.22);
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.055);
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

    &:focus-within {
      border-color: rgba(103, 232, 249, 0.6);
      background: rgba(255, 255, 255, 0.085);
      box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.12);
    }
  }

  .dev-port-host {
    min-width: 0;
    overflow: hidden;
    padding-left: 0.72rem;
    color: #94a3b8;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.68rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dev-port-input {
    width: 4.2rem;
    flex: 0 0 auto;
    padding: 0.55rem 0.65rem 0.55rem 0.12rem;
    border: 0;
    outline: 0;
    background: transparent;
    color: #f8fafc;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.78rem;
    font-weight: 750;
    line-height: 1;

    &::placeholder {
      color: #64748b;
      opacity: 0.82;
    }
  }

  .dev-port-submit {
    display: inline-flex;
    min-height: 2.65rem;
    align-items: center;
    justify-content: center;
    gap: 0.38rem;
    padding: 0 0.82rem;
    border: 0;
    border-radius: 11px;
    background: linear-gradient(135deg, #67e8f9, #38bdf8);
    color: #082f49;
    font-size: 0.73rem;
    font-weight: 850;
    white-space: nowrap;
    box-shadow: 0 8px 18px rgba(14, 165, 233, 0.2);
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

    &:hover,
    &:focus-visible {
      transform: translateY(-1px);
      filter: brightness(1.04);
      box-shadow: 0 11px 22px rgba(14, 165, 233, 0.28);
      outline: 3px solid rgba(103, 232, 249, 0.2);
      outline-offset: 2px;
    }

    &:active {
      transform: translateY(0) scale(0.98);
    }
  }

  .dev-port-error {
    margin: 0.5rem 0 0;
    color: #fda4af;
    font-size: 0.66rem;
    font-weight: 650;
    line-height: 1.4;
  }

  @keyframes devPortLauncherIn {
    from {
      opacity: 0;
      transform: translateY(-5px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  .navbar-brand {
    display: flex;
    align-items-center;
    font-weight: 700;
  }

  .navbar-brand img {
    height: 18px;
    margin-right: 10px;
    filter: ${({ $variant }) => ($variant === 'light' ? 'brightness(0) saturate(100%)' : 'none')};
    opacity: ${({ $variant }) => ($variant === 'light' ? '0.88' : '1')};
    transition: filter 0.25s ease, opacity 0.25s ease;
  }

  ${({ $variant }) => $variant === 'light' && css`
    @media (min-width: 992px) {
      & .mobile-btn-row {
        display: inline-flex;
        align-items: center;
        gap: 0.08rem;
        padding: 0;
        margin-left: 0.8rem;
        border: 0;
        background: transparent;
        box-shadow: none;
      }

      .terminal-access-btn {
        min-height: 2.55rem;
        margin-left: 0 !important;
        padding: 0.52rem 0.78rem !important;
        border: 0 !important;
        border-radius: 10px !important;
        background: transparent !important;
        color: #39434e !important;
        box-shadow: none;
        letter-spacing: -0.015em !important;

        svg {
          color: #71808e;
          transition: transform 0.25s ease, color 0.25s ease;
        }

        &:hover {
          transform: translateY(-1px);
          border: 0 !important;
          background: rgba(35, 54, 73, 0.055) !important;
          color: #18212b !important;
          box-shadow: none;

          svg {
            color: #315f91;
            transform: translateY(-1px);
          }
        }

        &:focus-visible {
          outline: 3px solid rgba(64, 116, 255, 0.2) !important;
          outline-offset: 2px;
          background: rgba(35, 54, 73, 0.055) !important;
          box-shadow: none !important;
        }

        &:active {
          transform: translateY(0) scale(0.98);
        }

        &.inquiry-btn {
          border: 0 !important;
          background: transparent !important;
          color: #39434e !important;

          svg {
            color: #5479a2;
          }

          &:hover {
            border: 0 !important;
            background: rgba(35, 54, 73, 0.055) !important;
            color: #18212b !important;
            box-shadow: none;

            svg {
              color: #315f91;
            }
          }
        }

        &.education-btn {
          svg {
            color: #4775ae;
          }

          &[aria-current='page'] {
            background: rgba(64, 116, 255, 0.075) !important;
            color: #294f82 !important;
          }
        }

        &.product-btn[aria-current='page'] {
          background: rgba(100, 87, 217, 0.07) !important;
          color: #50469c !important;
        }

        &.service-btn {
          position: relative;
          border: 0 !important;
          background: transparent !important;
          color: #39434e !important;
          box-shadow: none;

          svg {
            color: #54728b;
          }

          &:hover {
            border: 0 !important;
            background: rgba(35, 54, 73, 0.055) !important;
            color: #18212b !important;
            box-shadow: none;

            svg {
              color: #315f91;
              transform: translate(1px, -1px);
            }
          }
        }

        &.dev-service-btn {
          border: 1px solid rgba(30, 125, 80, 0.24) !important;
          background: rgba(246, 253, 248, 0.98) !important;
          color: #266a48 !important;
          box-shadow: 0 12px 28px rgba(38, 34, 27, 0.12);

          svg {
            color: #2b895a;
          }

          &:hover,
          &:focus-visible {
            border-color: rgba(30, 125, 80, 0.42) !important;
            background: #fff !important;
            color: #1d573b !important;
            box-shadow: 0 14px 30px rgba(38, 34, 27, 0.16);
          }
        }

        &.port-access-trigger {
          min-width: 7.25rem;
          padding-inline: 0.78rem !important;
          border: 1px solid rgba(49, 121, 167, 0.2) !important;
          background: rgba(248, 252, 255, 0.98) !important;
          color: #4a6278 !important;
          box-shadow: 0 12px 28px rgba(38, 34, 27, 0.11);

          svg {
            color: #4f7897;
          }

          &:hover,
          &:focus-visible,
          &[aria-expanded='true'] {
            border-color: rgba(49, 121, 167, 0.38) !important;
            background: #fff !important;
            color: #18212b !important;
            box-shadow: 0 14px 30px rgba(38, 34, 27, 0.15);
          }
        }
      }

      .port-access-wrap {
        margin-left: 0;
      }

      .dev-port-launcher {
        border-color: rgba(49, 77, 100, 0.14);
        background:
          radial-gradient(circle at 88% 0%, rgba(77, 157, 214, 0.11), transparent 44%),
          rgba(255, 255, 255, 0.97);
        box-shadow:
          0 18px 48px rgba(38, 34, 27, 0.16),
          0 1px 0 #fff inset;
      }

      .dev-port-launcher-heading {
        color: #27323e;
      }

      .dev-port-kicker {
        color: #3479a7;
      }

      .dev-port-close {
        border-color: rgba(49, 77, 100, 0.12);
        background: #f3f6f8;
        color: #607181;

        &:hover,
        &:focus-visible {
          background: #e9eff3;
          color: #263746;
          outline-color: rgba(64, 116, 255, 0.12);
        }
      }

      .dev-port-input-shell {
        border-color: rgba(49, 77, 100, 0.14);
        background: #f3f6f8;

        &:focus-within {
          border-color: rgba(49, 121, 167, 0.44);
          background: #fff;
          box-shadow: 0 0 0 3px rgba(49, 121, 167, 0.1);
        }
      }

      .dev-port-host {
        color: #71808e;
      }

      .dev-port-input {
        color: #1e2934;
      }

      .dev-port-submit {
        background: #23384c;
        color: #fff;
        box-shadow: 0 8px 18px rgba(35, 56, 76, 0.16);

        &:hover,
        &:focus-visible {
          box-shadow: 0 11px 22px rgba(35, 56, 76, 0.22);
          outline-color: rgba(64, 116, 255, 0.16);
        }
      }

      .dev-port-error {
        color: #b43c4d;
      }
    }
  `}

  /* 데스크탑에서 mobile-btn-row는 레이아웃에 영향 없이 투명하게 */
  .mobile-btn-row {
    display: contents;
  }

  @media (max-width: 991.98px) {
    .navbar-collapse {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      background: rgba(10, 11, 20, 0.95);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      /* 상단 패딩은 닫기 버튼 공간 확보, 나머지는 화면 높이 비례 */
      padding: clamp(3rem, 6vh, 4rem) clamp(1rem, 4vw, 2rem) clamp(1rem, 3vh, 2rem);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      border: none;
      margin: 0;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-20px);
      overflow-y: auto; /* 극단적으로 작은 화면 폴백 */
    }

    .navbar-collapse.show {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    .navbar-nav {
      width: 100%;
      /* 항목 간격을 화면 높이에 비례해 자동 조절 */
      gap: clamp(0.25rem, 1.8vh, 1.5rem);
      align-items: center;
    }

    .nav-link {
      /* 화면 높이에 따라 1rem ~ 1.5rem 사이에서 자동 조절 */
      font-size: clamp(1rem, 3.5vh, 1.5rem) !important;
      font-weight: 700 !important;
      color: #fff !important;
      /* 상하 패딩도 화면 높이 비례 */
      padding: clamp(0.3rem, 1.2vh, 1rem) 1rem !important;
      border: none !important;
      opacity: 0.8;
      transition: all 0.3s ease;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: clamp(0.1rem, 0.5vh, 0.2rem);
      width: auto !important;

      &::after {
        content: attr(data-label);
        font-size: clamp(0.65rem, 1.3vh, 0.8rem);
        font-weight: 400;
        color: var(--accent-color-1);
        opacity: 0.7;
      }

      &:active {
        transform: scale(0.95);
        color: var(--accent-color-1) !important;
      }
    }

    .terminal-access-btn {
      margin-left: 0 !important;
      margin-top: 0 !important;
      width: auto !important;
      max-width: none;
      /* 버튼 폰트/패딩도 화면 비례 */
      font-size: clamp(0.78rem, 1.8vh, 1rem) !important;
      padding: clamp(0.4rem, 1.1vh, 0.75rem) clamp(0.7rem, 2.5vw, 1.5rem) !important;
      justify-content: center !important;
      border-radius: 12px !important;
    }

    .service-access-wrap {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: clamp(0.35rem, 1vh, 0.6rem);
      flex: 0 0 100%;
      width: min(100%, 25rem);
    }

    .service-primary-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      align-items: stretch;
      gap: 0.22rem;
      width: 100%;
    }

    .service-primary-row > .service-btn {
      width: 100% !important;
    }

    .service-primary-row > .service-btn::before {
      display: block !important;
    }

    .mobile-dev-menu-toggle {
      display: grid;
      min-width: 2.8rem;
      min-height: 2.8rem;
      padding: 0;
      place-items: center;
      border: 1px solid ${({ $variant }) => (
        $variant === 'light'
          ? 'rgba(50, 91, 118, 0.1)'
          : 'rgba(137, 180, 207, 0.13)'
      )};
      border-radius: 12px;
      background: ${({ $variant }) => (
        $variant === 'light'
          ? 'rgba(65, 117, 151, 0.07)'
          : 'rgba(91, 154, 183, 0.1)'
      )};
      color: ${({ $variant }) => (
        $variant === 'light' ? '#55768d' : '#91b8ca'
      )};
      cursor: pointer;
      transition:
        background-color 0.22s ease,
        color 0.22s ease,
        box-shadow 0.22s ease;

      svg {
        width: 1rem;
        height: 1rem;
        transition: transform 0.22s ease;
      }

      &:hover,
      &:focus-visible,
      &[aria-expanded='true'] {
        border-color: ${({ $variant }) => (
          $variant === 'light'
            ? 'rgba(50, 123, 91, 0.18)'
            : 'rgba(88, 190, 139, 0.2)'
        )};
        background: ${({ $variant }) => (
          $variant === 'light'
            ? 'rgba(34, 139, 84, 0.1)'
            : 'rgba(54, 153, 104, 0.15)'
        )};
        color: ${({ $variant }) => (
          $variant === 'light' ? '#2f7050' : '#a7ddbf'
        )};
        outline: none;
        box-shadow: 0 0 0 3px ${({ $variant }) => (
          $variant === 'light'
            ? 'rgba(34, 139, 84, 0.08)'
            : 'rgba(54, 153, 104, 0.1)'
        )};
      }

      &[aria-expanded='true'] svg {
        transform: rotate(180deg);
      }
    }

    .terminal-access-btn.dev-service-btn {
      position: relative !important;
      top: auto;
      right: auto;
      width: min(100%, 11.2rem) !important;
      margin-top: 0 !important;
    }

    .dev-service-actions {
      position: relative;
      top: auto;
      right: auto;
      width: 100%;
      min-width: 0;
      gap: clamp(0.35rem, 1vh, 0.6rem);
      margin-top: 0.2rem;
      margin-bottom: calc(0.65rem + env(safe-area-inset-bottom));
    }

    .port-access-wrap {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      margin-left: 0;
    }

    .terminal-access-btn.port-access-trigger {
      width: 100% !important;
      min-width: 0;
      margin: 0 !important;
    }

    .dev-port-launcher {
      position: fixed;
      top: max(5.5rem, calc(env(safe-area-inset-top) + 4.25rem));
      right: 1rem;
      left: 1rem;
      z-index: 6200;
      grid-column: 1 / -1;
      width: min(26rem, calc(100vw - 2rem));
      max-height: calc(100dvh - 7rem);
      margin: 0 auto;
      padding: 0.78rem;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    .dev-port-launcher-heading {
      margin-bottom: 0.7rem;
    }

    .dev-port-launcher-row {
      grid-template-columns: minmax(0, 1fr) 4.75rem;
      gap: 0.5rem;
      align-items: stretch;
    }

    .dev-port-input-shell {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      align-content: center;
      gap: 0.28rem;
      min-height: 4rem;
      padding: 0.48rem 0.72rem 0.55rem;
    }

    .dev-port-host {
      width: 100%;
      overflow: visible;
      padding-left: 0;
      font-size: 0.62rem;
      line-height: 1.15;
      text-overflow: clip;
    }

    .dev-port-input {
      width: 100%;
      min-width: 0;
      min-height: 1.45rem;
      padding: 0;
      font-size: 1rem;
      line-height: 1.2;
      letter-spacing: 0.08em;
    }

    .dev-port-submit {
      min-height: 4rem;
      padding-inline: 0.65rem;
    }

    .mobile-btn-row {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: clamp(0.35rem, 1vh, 0.6rem);
      justify-content: center;
      margin-top: clamp(0.5rem, 1.8vh, 1.2rem);
      width: 100%;
    }
  }

  ${({ $variant }) => $variant === 'light' && css`
    @media (max-width: 991.98px) {
      .navbar-collapse {
        justify-content: flex-start;
        padding-top: clamp(5.75rem, 12vh, 7rem);
        background:
          radial-gradient(circle at 12% 8%, rgba(64, 116, 255, 0.12), transparent 32%),
          radial-gradient(circle at 92% 88%, rgba(235, 174, 119, 0.1), transparent 34%),
          rgba(248, 249, 248, 0.985);
        border-bottom: 1px solid rgba(24, 28, 35, 0.08);
      }

      .navbar-nav {
        gap: clamp(0.35rem, 1.25vh, 0.85rem);
      }

      .d-lg-none {
        display: grid;
        width: min(100%, 25rem) !important;
        gap: 0.18rem;
        padding: 0.45rem;
        border: 1px solid rgba(24, 28, 35, 0.08);
        border-radius: 18px;
        background: rgba(255, 255, 255, 0.52);
        box-shadow:
          0 1px 0 rgba(255, 255, 255, 0.9) inset,
          0 14px 40px rgba(38, 34, 27, 0.06);
      }

      .d-lg-none .nav-link {
        width: 100% !important;
        padding: clamp(0.58rem, 1.5vh, 0.82rem) 1rem !important;
        border-radius: 13px !important;
        color: #22252c !important;
        opacity: 1 !important;
        font-size: clamp(1rem, 2.6vh, 1.24rem) !important;
        letter-spacing: -0.025em;

        &::after {
          color: #6380a1;
          opacity: 0.78;
        }

        &:hover,
        &:focus-visible {
          background: rgba(255, 255, 255, 0.78);
          color: #315f96 !important;
          outline: none;
          box-shadow: 0 6px 20px rgba(31, 48, 66, 0.07);
        }

        &[aria-current='location'] {
          background: rgba(64, 116, 255, 0.1);
          color: #315f9f !important;
          box-shadow: 0 6px 20px rgba(64, 116, 255, 0.09);
        }
      }

      .mobile-btn-row {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        width: min(100%, 25rem);
        gap: 0.18rem;
        margin-top: clamp(0.35rem, 1vh, 0.75rem);
        padding: 2.75rem 0.55rem 0.55rem;
        border: 1px solid rgba(24, 28, 35, 0.08);
        border-radius: 18px;
        background: rgba(255, 255, 255, 0.58);
        box-shadow:
          0 1px 0 rgba(255, 255, 255, 0.92) inset,
          0 14px 40px rgba(38, 34, 27, 0.055);
      }

      .mobile-btn-row::before {
        content: 'QUICK ACCESS';
        position: absolute;
        top: 1rem;
        left: 1.2rem;
        color: #7c8997;
        font-size: 0.58rem;
        font-weight: 850;
        letter-spacing: 0.15em;
      }

      .service-access-wrap {
        width: 100%;
      }

      .service-access-wrap > .service-btn {
        width: 100% !important;
      }

      .terminal-access-btn {
        flex-direction: row !important;
        width: 100% !important;
        min-height: 3.1rem;
        justify-content: flex-start !important;
        margin: 0 !important;
        padding: 0.62rem 0.7rem !important;
        border: 0 !important;
        border-radius: 12px !important;
        background: transparent !important;
        color: #27323e !important;
        font-size: 0.86rem !important;
        line-height: 1.1;
        box-shadow: none;

        svg {
          width: 1.9rem;
          height: 1.9rem;
          padding: 0.47rem;
          border-radius: 9px;
          background: #edf2f7;
          color: #536a80;
          transition: transform 0.24s ease, background-color 0.24s ease, color 0.24s ease;
        }

        &:hover,
        &:focus-visible {
          transform: translateY(-1px);
          border: 0 !important;
          background: rgba(35, 54, 73, 0.055) !important;
          color: #17191f !important;
          outline: 3px solid rgba(64, 116, 255, 0.13);
          outline-offset: 1px;
          box-shadow: none;

          svg {
            transform: translateY(-1px);
          }
        }

        &.inquiry-btn {
          border: 0 !important;
          background: transparent !important;
          color: #27323e !important;

          svg {
            background: rgba(64, 116, 255, 0.12);
            color: #4074c9;
          }
        }

        &.education-btn {
          svg {
            background: rgba(64, 116, 255, 0.11);
            color: #4074c9;
          }

          &[aria-current='page'] {
            background: rgba(64, 116, 255, 0.075) !important;
            color: #315f9f !important;
          }
        }

        &.product-btn[aria-current='page'] {
          background: rgba(100, 87, 217, 0.065) !important;
          color: #50469c !important;
        }

        &.service-btn {
          min-height: 3.1rem;
          border: 0 !important;
          background: transparent !important;
          color: #27323e !important;
          box-shadow: none;

          svg {
            background: rgba(65, 117, 151, 0.1);
            color: #4a718e;
          }
        }
      }

      .terminal-access-btn > span {
        flex: 1;
        min-width: 0;
        text-align: left;
      }

      .terminal-access-btn::before {
        content: '';
        position: absolute;
        right: 0.9rem;
        width: 0.42rem;
        height: 0.42rem;
        border-top: 1.5px solid #9ba8b4;
        border-right: 1.5px solid #9ba8b4;
        transform: rotate(45deg);
        transition: transform 0.22s ease, border-color 0.22s ease;
      }

      .terminal-access-btn:hover::before,
      .terminal-access-btn:focus-visible::before {
        border-color: #4d6d8c;
        transform: translateX(2px) rotate(45deg);
      }

      .service-access-wrap {
        display: grid;
        grid-template-columns: 1fr;
        width: 100%;
        gap: 0.12rem;
      }

      .dev-service-actions {
        display: contents;
      }

      .dev-service-actions::before {
        display: none;
      }

      .dev-service-actions > .dev-service-btn {
        grid-column: 1;
        grid-row: 2;
      }

      .dev-service-actions > .port-access-wrap {
        display: contents;
      }

      .terminal-access-btn.dev-service-btn {
        min-height: 3.1rem;
        padding: 0.62rem 0.72rem !important;
        border: 0 !important;
        background: rgba(34, 139, 84, 0.07) !important;
        color: #2d6049 !important;

        svg {
          width: 1.9rem;
          height: 1.9rem;
          padding: 0.47rem;
          border-radius: 9px;
          background: rgba(34, 197, 94, 0.1);
          color: #358257;
        }

        &:hover,
        &:focus-visible,
        &[aria-expanded='true'] {
          border: 0 !important;
          background: rgba(34, 139, 84, 0.12) !important;
          color: #214d3a !important;
          outline-color: rgba(34, 139, 84, 0.12) !important;
        }
      }

      .terminal-access-btn.dev-service-btn {
        width: 100% !important;
      }

      .terminal-access-btn.port-access-trigger {
        grid-column: 1;
        grid-row: 2;
        min-height: 3.1rem;
        justify-content: flex-start !important;
        padding: 0.62rem 0.7rem !important;
        border: 0 !important;
        border-radius: 12px !important;
        background: transparent !important;
        color: #27323e !important;
        box-shadow: none !important;
        transform: none !important;

        svg {
          width: 1.9rem;
          height: 1.9rem;
          padding: 0.47rem;
          border-radius: 9px;
          background: rgba(65, 117, 151, 0.1);
          color: #4a718e;
        }

        &:hover,
        &:focus-visible,
        &[aria-expanded='true'] {
          border: 0 !important;
          background: rgba(35, 54, 73, 0.055) !important;
          color: #17191f !important;
          outline: 3px solid rgba(64, 116, 255, 0.13);
          outline-offset: 1px;
        }
      }

      .terminal-access-btn.dev-service-btn::before {
        display: none;
      }

      .terminal-access-btn.port-access-trigger[aria-expanded='true']::before {
        transform: translateY(-2px) rotate(135deg);
      }

      .dev-port-launcher {
        grid-column: 1 / -1;
        grid-row: 3;
        margin-top: 0;
      }

      .header-account {
        width: min(100%, 25rem);
        margin-top: 0.15rem !important;
      }
    }
  `}

  ${({ $variant }) => $variant === 'light' && css`
    .header-account {
      margin-left: 0.5rem;
    }

    .header-account .dropdown-toggle {
      min-height: 2.7rem;
      padding: 0.48rem 0.72rem !important;
      border: 1px solid rgba(24, 28, 35, 0.1);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.58);
      color: #252830 !important;
      box-shadow: 0 5px 18px rgba(38, 34, 27, 0.045);

      &:hover,
      &:focus-visible {
        border-color: rgba(100, 87, 217, 0.22);
        background: #fff;
        color: #17191f !important;
        outline: 3px solid rgba(100, 87, 217, 0.13);
        outline-offset: 1px;
        box-shadow: 0 8px 24px rgba(38, 34, 27, 0.08);
      }
    }

    .header-account-menu {
      margin-top: 0.55rem !important;
      padding: 0.45rem !important;
      border: 1px solid rgba(24, 28, 35, 0.1) !important;
      border-radius: 14px !important;
      background: rgba(255, 255, 255, 0.96) !important;
      box-shadow: 0 16px 42px rgba(38, 34, 27, 0.13) !important;
      backdrop-filter: blur(18px);
    }

    .header-account-menu .dropdown-item {
      padding: 0.68rem 0.8rem;
      border-radius: 9px;
      color: #252830 !important;
      font-weight: 650;

      &:hover,
      &:focus {
        background: rgba(100, 87, 217, 0.09);
        color: #4d43ae !important;
      }
    }

    @media (max-width: 991.98px) {
      .header-account {
        margin-left: 0;
      }

      .header-account .dropdown-toggle {
        width: 100%;
        justify-content: center;
      }
    }
  `}

  ${({ $variant, $productPage }) => $variant === 'dark' && $productPage && css`
    @media (min-width: 992px) {
      & .mobile-btn-row {
        display: contents;
      }

      .terminal-access-btn:not(.dev-service-btn) {
        min-height: 2.55rem;
        margin-left: 0 !important;
        padding: 0.52rem 0.78rem !important;
        border: 0 !important;
        border-radius: 10px !important;
        background: transparent !important;
        color: rgba(229, 235, 244, 0.76) !important;
        box-shadow: none !important;
        letter-spacing: -0.015em !important;
        transform: none !important;
        transition:
          background-color 0.22s ease,
          color 0.22s ease !important;

        svg {
          color: #8291a6;
          transform: none !important;
          transition: color 0.22s ease !important;
        }

        &:hover,
        &:focus-visible {
          border: 0 !important;
          background: rgba(255, 255, 255, 0.07) !important;
          color: #fff !important;
          box-shadow: none !important;
          transform: none !important;

          svg {
            color: #c1cee0;
            transform: none !important;
          }
        }

        &:focus-visible {
          outline: 3px solid rgba(126, 163, 255, 0.2) !important;
          outline-offset: 1px;
        }

        &:active {
          background: rgba(255, 255, 255, 0.1) !important;
          transform: none !important;
        }

        &.education-btn svg {
          color: #82aee8;
        }

        &.product-btn {
          &[aria-current='page'] {
            background: rgba(167, 139, 250, 0.11) !important;
            color: #eee9ff !important;
          }

          svg {
            color: #aa9be8;
          }
        }

        &.inquiry-btn svg {
          color: #89a6ce;
        }

        &.service-btn svg {
          color: #7fa8bd;
        }
      }
    }

    @media (max-width: 991.98px) {
      .navbar-collapse {
        height: 100svh;
        max-height: 100svh;
        justify-content: flex-start;
        padding-top: clamp(5.15rem, 10vh, 6.3rem);
        background:
          radial-gradient(circle at 12% 6%, rgba(93, 128, 211, 0.16), transparent 34%),
          radial-gradient(circle at 92% 92%, rgba(109, 76, 180, 0.13), transparent 38%),
          rgba(7, 10, 17, 0.985);
        border-bottom: 1px solid rgba(224, 232, 244, 0.09);
        overscroll-behavior: contain;
      }

      .navbar-nav {
        gap: clamp(0.28rem, 0.9vh, 0.65rem);
      }

      .d-lg-none {
        display: grid;
        width: min(100%, 25rem) !important;
        gap: 0.08rem;
        padding: 0.4rem;
        border: 1px solid rgba(224, 232, 244, 0.1);
        border-radius: 18px;
        background: rgba(20, 25, 37, 0.62);
        box-shadow:
          0 1px 0 rgba(255, 255, 255, 0.055) inset,
          0 20px 52px rgba(0, 0, 0, 0.24);
        backdrop-filter: blur(18px) saturate(130%);
        -webkit-backdrop-filter: blur(18px) saturate(130%);
      }

      .d-lg-none .nav-link {
        width: 100% !important;
        min-height: clamp(2.58rem, 5.7vh, 2.95rem);
        justify-content: center;
        padding: 0.32rem 0.85rem !important;
        border-radius: 12px !important;
        color: rgba(238, 243, 250, 0.9) !important;
        opacity: 1 !important;
        font-size: clamp(0.9rem, 2.15vh, 1.05rem) !important;
        line-height: 1.05;
        letter-spacing: -0.025em;

        &::after {
          color: #8099bd;
          font-size: clamp(0.54rem, 1.15vh, 0.65rem);
          opacity: 0.82;
        }

        &:hover,
        &:focus-visible {
          background: rgba(255, 255, 255, 0.065);
          color: #fff !important;
          outline: none;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
        }

        &[aria-current='location'] {
          background: rgba(133, 117, 218, 0.15);
          color: #f4f0ff !important;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.055) inset,
            0 8px 24px rgba(66, 48, 129, 0.18);
        }
      }

      .mobile-btn-row {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        width: min(100%, 25rem);
        gap: 0.12rem;
        margin-top: clamp(0.25rem, 0.8vh, 0.6rem);
        padding: 2.55rem 0.5rem 0.5rem;
        border: 1px solid rgba(224, 232, 244, 0.1);
        border-radius: 18px;
        background: rgba(20, 25, 37, 0.66);
        box-shadow:
          0 1px 0 rgba(255, 255, 255, 0.055) inset,
          0 20px 52px rgba(0, 0, 0, 0.22);
        backdrop-filter: blur(18px) saturate(130%);
        -webkit-backdrop-filter: blur(18px) saturate(130%);
      }

      .mobile-btn-row::before {
        content: 'QUICK ACCESS';
        position: absolute;
        top: 0.9rem;
        left: 1.15rem;
        color: #74849a;
        font-size: 0.56rem;
        font-weight: 850;
        letter-spacing: 0.16em;
      }

      .terminal-access-btn:not(.dev-service-btn):not(.port-access-trigger) {
        position: relative;
        flex-direction: row !important;
        width: 100% !important;
        min-height: 2.8rem;
        justify-content: flex-start !important;
        margin: 0 !important;
        padding: 0.52rem 0.68rem !important;
        border: 0 !important;
        border-radius: 12px !important;
        background: transparent !important;
        color: rgba(235, 240, 247, 0.82) !important;
        font-size: 0.84rem !important;
        line-height: 1.1;
        box-shadow: none !important;
        transform: none !important;
        transition:
          background-color 0.22s ease,
          color 0.22s ease !important;

        svg {
          width: 1.85rem;
          height: 1.85rem;
          padding: 0.45rem;
          border-radius: 9px;
          background: rgba(128, 151, 184, 0.12);
          color: #91a8c7;
          transform: none !important;
        }

        &:hover,
        &:focus-visible,
        &:active {
          border: 0 !important;
          background: rgba(255, 255, 255, 0.07) !important;
          color: #fff !important;
          box-shadow: none !important;
          transform: none !important;

          svg {
            background: rgba(128, 151, 184, 0.17);
            color: #c6d2e1;
            transform: none !important;
          }
        }

        &.education-btn svg {
          background: rgba(91, 143, 224, 0.14);
          color: #82aee8;
        }

        &.product-btn[aria-current='page'] {
          background: rgba(167, 139, 250, 0.11) !important;
          color: #eee9ff !important;

          svg {
            background: rgba(167, 139, 250, 0.14);
            color: #b3a5ee;
          }
        }

        &.inquiry-btn svg {
          background: rgba(103, 151, 212, 0.13);
          color: #8eb0dc;
        }

        &.service-btn svg {
          background: rgba(91, 154, 183, 0.13);
          color: #87afc3;
        }
      }

      .terminal-access-btn > span {
        flex: 1;
        min-width: 0;
        text-align: left;
      }

      .terminal-access-btn:not(.dev-service-btn)::before {
        content: '';
        position: absolute;
        right: 0.9rem;
        width: 0.42rem;
        height: 0.42rem;
        border-top: 1.5px solid #637286;
        border-right: 1.5px solid #637286;
        transform: rotate(45deg);
        transition: transform 0.22s ease, border-color 0.22s ease;
      }

      .terminal-access-btn:not(.dev-service-btn):hover::before,
      .terminal-access-btn:not(.dev-service-btn):focus-visible::before {
        border-color: #aab9cc;
        transform: translateX(2px) rotate(45deg);
      }

      .service-access-wrap {
        display: grid;
        grid-template-columns: 1fr;
        width: 100%;
        gap: 0.12rem;
      }

      .service-access-wrap > .service-btn {
        width: 100% !important;
      }

      .dev-service-actions {
        display: contents;
      }

      .dev-service-actions::before {
        display: none;
      }

      .dev-service-actions > .dev-service-btn {
        grid-column: 1;
        grid-row: 2;
      }

      .dev-service-actions > .port-access-wrap {
        display: contents;
      }

      .terminal-access-btn.dev-service-btn {
        width: 100% !important;
        min-height: 2.8rem;
        padding: 0.55rem 0.66rem !important;
        border: 0 !important;
        border-radius: 12px !important;
        background: rgba(54, 153, 104, 0.1) !important;
        color: #a7ddbf !important;
        box-shadow: none;
      }

      .terminal-access-btn.port-access-trigger {
        grid-column: 1;
        grid-row: 2;
      }

      .terminal-access-btn.port-access-trigger[aria-expanded='true'] {
        background: rgba(255, 255, 255, 0.07) !important;
        color: #fff !important;
      }

      .terminal-access-btn.port-access-trigger[aria-expanded='true']::before {
        transform: translateY(-2px) rotate(135deg);
      }

      .dev-port-launcher {
        grid-column: 1 / -1;
        grid-row: 3;
        margin-top: 0;
      }

      .header-account {
        width: min(100%, 25rem);
        margin-top: 0.15rem !important;
      }
    }
  `}
`;

const CloseButton = styled.button`
  display: none;
  @media (max-width: 991.98px) {
    display: block;
    position: absolute;
    top: 2rem;
    right: 2rem;
    background: none;
    border: none;
    color: #fff;
    font-size: 2rem;
    cursor: pointer;
    z-index: 2100;

    ${({ $variant }) => $variant === 'light' && css`
      width: 2.75rem;
      height: 2.75rem;
      display: grid;
      place-items: center;
      padding: 0;
      background: rgba(255, 255, 255, 0.72);
      border: 1px solid rgba(24, 28, 35, 0.1);
      border-radius: 12px;
      color: #252830;
      font-size: 1.8rem;
      line-height: 1;
      transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

      &:hover,
      &:focus-visible {
        transform: rotate(3deg);
        background: #fff;
        outline: none;
        box-shadow: 0 8px 24px rgba(38, 34, 27, 0.1);
      }
    `}

    ${({ $variant, $productPage }) => $variant === 'dark' && $productPage && css`
      width: 2.75rem;
      height: 2.75rem;
      display: grid;
      place-items: center;
      padding: 0;
      border: 1px solid rgba(224, 232, 244, 0.12);
      border-radius: 12px;
      background: rgba(20, 25, 37, 0.72);
      color: #f2f5f9;
      font-size: 1.8rem;
      line-height: 1;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.06) inset,
        0 10px 30px rgba(0, 0, 0, 0.22);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

      &:hover,
      &:focus-visible {
        transform: rotate(3deg);
        background: rgba(30, 37, 53, 0.94);
        outline: none;
        box-shadow:
          0 1px 0 rgba(255, 255, 255, 0.08) inset,
          0 12px 34px rgba(0, 0, 0, 0.3);
      }
    `}
  }
`;

const InquiryOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: ${({ $variant }) => (
    $variant === 'light'
      ? 'rgba(16, 27, 43, 0.42)'
      : 'rgba(0, 0, 0, 0.7)'
  )};
  backdrop-filter: ${({ $variant }) => ($variant === 'light' ? 'blur(18px)' : 'blur(8px)')};
  -webkit-backdrop-filter: ${({ $variant }) => (
    $variant === 'light' ? 'blur(18px)' : 'blur(8px)'
  )};
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  ${({ $variant }) => $variant === 'light' && css`
    background:
      radial-gradient(circle at 22% 12%, rgba(137, 190, 248, 0.25), transparent 34%),
      rgba(13, 24, 39, 0.46);
  `}

  @media (max-width: 576px) {
    align-items: flex-start;
    padding: 0.65rem;
    overflow-y: auto;
  }
`;

const InquiryModal = styled.div`
  text-align: left;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1a2e 100%);
  border: 1px solid rgba(0, 210, 255, 0.2);
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 540px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 210, 255, 0.1);
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  .inquiry-heading-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }

  .inquiry-kicker {
    display: none;
  }

  .inquiry-close {
    display: grid;
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    padding: 0;
    place-items: center;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 50%;
    background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.55);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover,
    &:focus-visible {
      background: rgba(255,255,255,0.1);
      color: #fff;
      outline: none;
    }
  }

  h2 {
    color: #fff;
    font-size: 1.5rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
  }

  p.subtitle {
    color: rgba(255,255,255,0.5);
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
  }

  .inquiry-benefits {
    display: none;
  }

  .inquiry-field-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 1rem;
  }

  label {
    display: block;
    color: rgba(255,255,255,0.7);
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
    margin-top: 1rem;
  }

  input, select, textarea {
    width: 100%;
    padding: 0.7rem 1rem;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 10px;
    color: #fff;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s;
    &:focus { border-color: rgba(0, 210, 255, 0.6); }
    &::placeholder { color: rgba(255,255,255,0.3); }
  }

  select {
    cursor: pointer;
    option { background: #1a1a2e; color: #fff; }
  }

  textarea { resize: vertical; min-height: 100px; }

  .required { color: #ff6b6b; }

  .btn-row {
    display: flex;
    gap: 0.8rem;
    margin-top: 1.5rem;
  }

  .btn-cancel {
    flex: 1;
    padding: 0.8rem;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 12px;
    color: rgba(255,255,255,0.7);
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: rgba(255,255,255,0.1); }
  }

  .btn-submit {
    flex: 2;
    padding: 0.8rem;
    background: linear-gradient(135deg, #00d2ff, #0080ff);
    border: none;
    border-radius: 12px;
    color: #fff;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(0, 210, 255, 0.3); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
  }

  .success-message {
    text-align: center;
    padding: 2rem 0;
    .icon { font-size: 3rem; color: #4cd137; margin-bottom: 1rem; }
    h3 { color: #fff; font-size: 1.3rem; margin-bottom: 0.5rem; }
    p { color: rgba(255,255,255,0.6); font-size: 0.9rem; }
  }

  ${({ $variant }) => $variant === 'light' && css`
    position: relative;
    max-width: 720px;
    max-height: calc(100vh - 2rem);
    padding: 2rem;
    overflow: hidden auto;
    border: 1px solid rgba(117, 154, 198, 0.24);
    border-radius: 28px;
    background:
      radial-gradient(circle at 92% 4%, rgba(117, 180, 245, 0.16), transparent 31%),
      linear-gradient(160deg, rgba(252, 254, 255, 0.99), rgba(241, 247, 253, 0.98));
    color: #182c46;
    box-shadow:
      0 38px 90px rgba(10, 31, 58, 0.28),
      0 1px 0 rgba(255, 255, 255, 0.92) inset;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 2rem;
      right: 2rem;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(78, 141, 211, 0.65), transparent);
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 999px;
      background: rgba(73, 112, 157, 0.18);
    }

    .inquiry-kicker {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      color: #4676aa;
      font-size: 9px;
      font-weight: 850;
      letter-spacing: 0.14em;
    }

    .inquiry-kicker::before {
      content: "";
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #65a7e4;
      box-shadow: 0 0 0 5px rgba(101, 167, 228, 0.11);
    }

    h2 {
      margin: 0;
      color: #172b44;
      font-size: 1.65rem;
      font-weight: 790;
      line-height: 1.14;
      letter-spacing: -0.04em;
    }

    p.subtitle {
      max-width: 580px;
      margin: 0.65rem 0 1.15rem;
      color: #667c96;
      font-size: 0.86rem;
      line-height: 1.58;
      letter-spacing: -0.015em;
    }

    .inquiry-close {
      border-color: rgba(73, 112, 157, 0.14);
      background: rgba(255, 255, 255, 0.72);
      color: #59728e;
      box-shadow: 0 7px 18px rgba(39, 77, 120, 0.07);
    }

    .inquiry-close:hover,
    .inquiry-close:focus-visible {
      border-color: rgba(63, 111, 168, 0.25);
      background: #fff;
      color: #1e4b7b;
      box-shadow: 0 10px 24px rgba(39, 77, 120, 0.13);
    }

    .inquiry-benefits {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-bottom: 0.25rem;
      padding: 10px;
      border: 1px solid rgba(86, 132, 184, 0.13);
      border-radius: 15px;
      background: rgba(222, 238, 252, 0.46);
    }

    .inquiry-benefits span {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      min-height: 26px;
      color: #54708f;
      font-size: 10px;
      font-weight: 680;
      text-align: center;
      white-space: nowrap;
    }

    .inquiry-benefits span::before {
      content: "✓";
      display: grid;
      width: 16px;
      height: 16px;
      place-items: center;
      border-radius: 50%;
      background: #fff;
      color: #3777b9;
      font-size: 9px;
      box-shadow: 0 4px 10px rgba(43, 91, 143, 0.1);
    }

    label {
      text-align: left;
      margin-top: 0.85rem;
      margin-bottom: 0.38rem;
      color: #405a77;
      font-size: 0.76rem;
      font-weight: 720;
    }

    input,
    select,
    textarea {
      min-height: 46px;
      padding: 0.7rem 0.9rem;
      border: 1px solid rgba(75, 114, 160, 0.17);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.84);
      color: #182d48;
      font-size: 0.86rem;
      box-shadow: 0 1px 0 #fff inset;
      transition:
        border-color 0.2s ease,
        background-color 0.2s ease,
        box-shadow 0.2s ease;

      &::placeholder {
        color: #9cabbc;
      }

      &:hover {
        border-color: rgba(75, 114, 160, 0.28);
      }

      &:focus {
        border-color: rgba(51, 111, 181, 0.55);
        background: #fff;
        box-shadow: 0 0 0 4px rgba(65, 129, 208, 0.09);
      }
    }

    select {
      color: #28435f;

      option {
        background: #fff;
        color: #243b55;
      }
    }

    textarea {
      min-height: 102px;
      line-height: 1.55;
    }

    .required {
      color: #3579bd;
    }

    .btn-row {
      gap: 0.7rem;
      margin-top: 1.15rem;
    }

    .btn-cancel,
    .btn-submit {
      min-height: 48px;
      border-radius: 13px;
      font-size: 0.86rem;
      transition:
        transform 0.2s ease,
        border-color 0.2s ease,
        background-color 0.2s ease,
        box-shadow 0.2s ease;
    }

    .btn-cancel {
      border-color: rgba(70, 106, 148, 0.16);
      background: rgba(255, 255, 255, 0.72);
      color: #60758d;
    }

    .btn-cancel:hover {
      border-color: rgba(70, 106, 148, 0.26);
      background: #fff;
      color: #2d4d70;
      transform: translateY(-1px);
    }

    .btn-submit {
      background: linear-gradient(135deg, #183f6d 0%, #347bc0 100%);
      color: #fff;
      box-shadow: 0 13px 26px rgba(31, 78, 132, 0.2);
    }

    .btn-submit:hover {
      transform: translateY(-2px);
      box-shadow: 0 17px 34px rgba(31, 78, 132, 0.28);
    }

    .btn-submit:focus-visible,
    .btn-cancel:focus-visible {
      outline: 3px solid rgba(58, 123, 199, 0.2);
      outline-offset: 3px;
    }

    .success-message .icon {
      display: grid;
      width: 66px;
      height: 66px;
      margin: 0 auto 1rem;
      place-items: center;
      border-radius: 22px;
      background: linear-gradient(135deg, #eaf5ff, #d9ebff);
      color: #2d72b5;
      font-size: 2rem;
      box-shadow: 0 15px 30px rgba(45, 103, 165, 0.14);
    }

    .success-message h3 {
      color: #172b44;
      font-size: 1.45rem;
    }

    .success-message p {
      color: #677c95;
    }
  `}

  @media (max-width: 576px) {
    padding: 1.5rem;
    h2 { font-size: 1.3rem; }

    .inquiry-field-grid {
      grid-template-columns: 1fr;
      gap: 0;
    }

    ${({ $variant }) => $variant === 'light' && css`
      max-height: none;
      min-height: calc(100vh - 1.3rem);
      padding: 1.35rem 1.1rem;
      border-radius: 22px;

      .inquiry-benefits {
        grid-template-columns: 1fr;
        gap: 5px;
      }

      .inquiry-benefits span {
        justify-content: flex-start;
        padding: 0 7px;
      }
    `}
  }
`;

const StyledToggle = styled(Navbar.Toggle)`
  border: 1px solid ${({ $variant }) => (
    $variant === 'light' ? 'rgba(20, 24, 31, 0.14)' : 'var(--glass-border)'
  )} !important;
  border-radius: 10px;
  background: ${({ $variant }) => (
    $variant === 'light' ? 'rgba(255, 255, 255, 0.58)' : 'rgba(255, 255, 255, 0.05)'
  )} !important;
  transition: all 0.3s ease;
  padding: 0.5rem 0.75rem;

  &:hover, &:focus {
    background: ${({ $variant }) => (
      $variant === 'light' ? '#fff' : 'rgba(255, 255, 255, 0.1)'
    )} !important;
    border-color: ${({ $variant }) => (
      $variant === 'light' ? 'rgba(100, 87, 217, 0.42)' : 'var(--accent-color-2)'
    )} !important;
    box-shadow: ${({ $variant }) => (
      $variant === 'light'
        ? '0 6px 20px rgba(38, 34, 27, 0.1)'
        : '0 0 15px rgba(0, 210, 255, 0.3)'
    )};
  }

  ${({ $variant, $productPage }) => $variant === 'dark' && $productPage && css`
    width: 2.85rem;
    height: 2.85rem;
    display: grid;
    place-items: center;
    padding: 0;
    border-color: rgba(224, 232, 244, 0.13) !important;
    border-radius: 13px;
    background:
      linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.025)),
      rgba(11, 15, 23, 0.7) !important;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.07) inset,
      0 10px 28px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);

    &:hover,
    &:focus-visible {
      border-color: rgba(161, 183, 220, 0.28) !important;
      background: rgba(23, 29, 42, 0.92) !important;
      outline: 3px solid rgba(126, 163, 255, 0.13);
      outline-offset: 2px;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.08) inset,
        0 12px 32px rgba(0, 0, 0, 0.28);
    }

    &[aria-expanded='true'] {
      opacity: 0;
      pointer-events: none;
    }
  `}

  .navbar-toggler-icon {
    width: ${({ $productPage }) => ($productPage ? '1.28rem' : '1.5em')};
    height: ${({ $productPage }) => ($productPage ? '1.28rem' : '1.5em')};
    background-image: ${({ $variant, $productPage }) => {
      if ($productPage) {
        return `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(238, 243, 250, 0.94)' stroke-linecap='round' stroke-width='1.8' d='M5 8h20M5 15h20M5 22h20'/%3e%3c/svg%3e")`;
      }
      return $variant === 'light'
        ? `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(23, 26, 32, 0.9)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e")`
        : `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(255, 255, 255, 0.9)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e")`;
    }} !important;
  }
`;

const FloatingNav = styled.nav`
  position: fixed;
  right: var(--floating-ui-right, 1.5rem);
  top: 50%;
  transform: translateY(-50%);
  width: var(--floating-action-size, 48px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
  z-index: 1000;
  pointer-events: none;

  &[data-theme='light'] {
    top: calc(50% - clamp(4px, 1.2vh, 10px));
    right: var(--floating-ui-right, 1.5rem);
    gap: 3px;
    max-height: calc(100vh - 92px);
    padding: 6px 4px;
    border: 1px solid rgba(24, 28, 35, 0.09);
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.74);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.94) inset,
      0 16px 42px rgba(37, 46, 58, 0.12);
    backdrop-filter: blur(20px) saturate(150%);
    -webkit-backdrop-filter: blur(20px) saturate(150%);
    pointer-events: auto;
  }

  &[data-page='product'] {
    top: calc(50% - clamp(56px, 8vh, 68px));
    right: var(--floating-ui-right, 1.5rem);
    gap: 3px;
    max-height: calc(100vh - 92px);
    padding: 6px 4px;
    border: 1px solid rgba(224, 232, 244, 0.09);
    border-radius: 20px;
    background: rgba(11, 15, 23, 0.7);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.055) inset,
      0 18px 46px rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(20px) saturate(135%);
    -webkit-backdrop-filter: blur(20px) saturate(135%);
    pointer-events: auto;
  }

  &[data-page='education'] {
    top: calc(50% - clamp(56px, 8vh, 68px));
  }

  @media (max-width: 991.98px) {
    right: var(--floating-ui-right, 0.75rem);
    gap: 1.8rem;
  }

  @media (max-width: 860px) {
    &[data-page='showcase'] {
      top: auto;
      right: auto;
      bottom: max(0.75rem, env(safe-area-inset-bottom));
      left: 50%;
      width: auto;
      max-height: none;
      flex-direction: row;
      gap: 1px;
      transform: translate3d(-50%, 0, 0);
      padding: 4px;
      border-radius: 14px;
      background: rgba(250, 249, 246, 0.76);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.82) inset,
        0 12px 30px rgba(37, 46, 58, 0.12);
    }
  }

  @media (max-width: 620px) {
    &[data-theme='light'] {
      top: calc(50% - 8px);
      right: var(--floating-ui-right, max(0.35rem, env(safe-area-inset-right)));
      left: auto;
      display: flex;
      flex-direction: column;
      gap: 0;
      max-height: calc(100vh - 136px);
      transform: translate3d(0, -50%, 0);
      padding: 4px 3px;
      border-color: rgba(24, 28, 35, 0.065);
      border-radius: 14px;
      background: rgba(250, 249, 246, 0.48);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.68) inset,
        0 10px 28px rgba(37, 46, 58, 0.075);
      backdrop-filter: blur(18px) saturate(135%);
      -webkit-backdrop-filter: blur(18px) saturate(135%);

      @supports (height: 100svh) {
        top: calc(50svh - 8px);
        max-height: calc(100svh - 136px);
      }
    }

    &[data-page='product'] {
      top: calc(50% - 8px);
      right: var(--floating-ui-right, max(0.35rem, env(safe-area-inset-right)));
      left: auto;
      display: flex;
      flex-direction: column;
      gap: 0;
      max-height: calc(100vh - 136px);
      transform: translate3d(0, -50%, 0);
      padding: 4px 3px;
      border-color: rgba(224, 232, 244, 0.075);
      border-radius: 14px;
      background: rgba(10, 14, 22, 0.5);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.045) inset,
        0 12px 32px rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(18px) saturate(125%);
      -webkit-backdrop-filter: blur(18px) saturate(125%);

      @supports (height: 100svh) {
        top: calc(50svh - 8px);
        max-height: calc(100svh - 136px);
      }
    }

    &[data-page='showcase'] {
      top: auto;
      right: auto;
      bottom: max(0.75rem, env(safe-area-inset-bottom));
      left: 50%;
      width: auto;
      max-height: none;
      flex-direction: row;
      gap: 1px;
      transform: translate3d(-50%, 0, 0);
      padding: 4px;
      border-radius: 14px;
      background: rgba(250, 249, 246, 0.76);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.82) inset,
        0 12px 30px rgba(37, 46, 58, 0.12);
    }


    &[data-page='education'] {
      top: calc(50% - 8px);
    }
  }

  @media (max-height: 680px) and (min-width: 621px) {
    &[data-theme='light'],
    &[data-page='product'],
    &[data-page='education'] {
      top: calc(50% - 92px);
      gap: 2px;
      padding: 5px 4px;
    }

    &[data-page='showcase'] {
      top: auto;
    }
  }

  @media (max-width: 620px) and (max-height: 620px) {
    &[data-theme='light'],
    &[data-page='product'],
    &[data-page='education'] {
      top: calc(50% - 66px);

      @supports (height: 100svh) {
        top: calc(50svh - 66px);
      }
    }

    &[data-page='showcase'] {
      top: auto;
    }
  }
`;

const NavItem = styled.a`
  pointer-events: auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  padding: 6px;

  @media (max-width: 991.98px) {
    padding: 8px;
  }

  .nav-index {
    display: none;
  }

  .label {
    position: absolute;
    right: calc(100% + 0.6rem);
    top: 50%;
    transform: translateY(-50%) translateX(6px);
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    background: ${({ $variant }) => (
      $variant === 'light' ? 'rgba(252, 250, 246, 0.94)' : 'rgba(0, 210, 255, 0.25)'
    )};
    backdrop-filter: blur(15px);
    border: 1px solid ${({ $variant }) => (
      $variant === 'light' ? 'rgba(24, 28, 35, 0.12)' : 'rgba(0, 210, 255, 0.4)'
    )};
    padding: 0.55rem 1.2rem 0.45rem;
    border-radius: 10px;
    color: ${({ $variant }) => ($variant === 'light' ? '#24272e' : '#fff')};
    font-size: 0.85rem;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: ${({ $variant }) => (
      $variant === 'light'
        ? '0 8px 25px rgba(38, 34, 27, 0.12)'
        : '0 8px 25px rgba(0,0,0,0.4)'
    )};
    pointer-events: none;
    line-height: 1;

    &.show-temp {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }
  }

  .dot {
    width: 12px;
    height: 12px;
    background: ${({ $active, $variant }) => (
      $active
        ? 'var(--accent-color-1)'
        : ($variant === 'light' ? 'rgba(28, 32, 40, 0.28)' : 'rgba(255, 255, 255, 0.2)')
    )};
    border: 2px solid ${({ $active, $variant }) => (
      $active
        ? 'rgba(0, 210, 255, 0.5)'
        : ($variant === 'light' ? 'rgba(28, 32, 40, 0.13)' : 'rgba(255, 255, 255, 0.1)')
    )};
    border-radius: 50%;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    flex-shrink: 0;
    transform: ${({ $active }) => ($active ? 'scale(1.3)' : 'scale(1)')};
    box-shadow: ${({ $active }) => ($active ? '0 0 15px var(--accent-color-1)' : 'none')};

    @media (max-width: 991.98px) {
      width: 10px;
      height: 10px;
    }
  }

  &:hover {
    .dot {
      background: var(--accent-color-1);
      border-color: rgba(0, 210, 255, 0.5);
      transform: scale(1.3);
      box-shadow: 0 0 15px var(--accent-color-1);
    }
  }

  ${({ $variant, $active }) => $variant === 'light' && css`
    width: 38px;
    height: 40px;
    padding: 0;
    border-radius: 13px;
    background: ${$active ? '#171b22' : 'transparent'};
    box-shadow: ${$active ? '0 8px 18px rgba(23, 27, 34, 0.18)' : 'none'};
    transition:
      background-color 0.24s ease,
      box-shadow 0.24s ease,
      transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);

    .nav-index {
      display: block;
      color: ${$active ? '#fff' : '#7f8791'};
      font-size: 9px;
      font-weight: 820;
      letter-spacing: 0.06em;
      transition: color 0.24s ease;
    }

    .dot {
      position: absolute;
      bottom: 7px;
      width: ${$active ? '15px' : '5px'};
      height: 2px;
      border: 0;
      border-radius: 999px;
      background: ${$active ? '#7eb1d7' : '#b7bec7'};
      box-shadow: none;
      transform: none;
      transition:
        width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
        background-color 0.24s ease;
    }

    .label {
      right: calc(100% + 0.8rem);
      padding: 0.65rem 0.9rem 0.6rem;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      background: rgba(22, 26, 33, 0.94);
      color: #fff;
      font-size: 0.76rem;
      box-shadow: 0 12px 28px rgba(22, 26, 33, 0.2);
    }

    .label.show-temp {
      opacity: 0;
      transform: translateY(-50%) translateX(6px);
    }

    &:hover,
    &:focus-visible {
      background: ${$active ? '#171b22' : 'rgba(30, 38, 48, 0.065)'};
      transform: translateX(-2px);
    }

    &:focus-visible {
      outline: 3px solid #005fcc;
      outline-offset: 3px;
    }

    &:hover .label,
    &:focus-visible .label {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }

    &:hover .dot,
    &:focus-visible .dot {
      width: 15px;
      border: 0;
      background: ${$active ? '#7eb1d7' : '#72879a'};
      box-shadow: none;
      transform: none;
    }

    @media (max-width: 620px) {
      width: 34px;
      height: 36px;
      border-radius: 10px;
      background: ${$active ? 'rgba(23, 27, 34, 0.92)' : 'transparent'};
      box-shadow: ${$active ? '0 6px 14px rgba(23, 27, 34, 0.16)' : 'none'};

      .label {
        display: none;
      }

      .nav-index {
        display: block;
        opacity: ${$active ? '1' : '0'};
        color: #fff;
        font-size: 7px;
        transform: translateY(-2px);
        transition: opacity 0.2s ease;
      }

      .dot {
        bottom: 7px;
        width: ${$active ? '12px' : '4px'};
        height: 2px;
        background: ${$active ? '#8fc4ea' : '#9da6b0'};
      }

      &:hover,
      &:focus-visible {
        transform: none;
      }

      &:hover .dot,
      &:focus-visible .dot {
        width: ${$active ? '12px' : '8px'};
        background: ${$active ? '#8fc4ea' : '#71869a'};
      }
    }

    @media (max-height: 680px) and (min-width: 621px) {
      height: 34px;
    }

    @media (max-width: 620px) and (max-height: 620px) {
      height: 30px;
    }
  `}

  ${({ $productPage, $active }) => $productPage && css`
    width: 38px;
    height: 40px;
    padding: 0;
    border-radius: 13px;
    background: ${$active ? 'rgba(239, 244, 251, 0.96)' : 'transparent'};
    box-shadow: ${$active ? '0 9px 22px rgba(0, 0, 0, 0.3)' : 'none'};
    transition:
      background-color 0.24s ease,
      box-shadow 0.24s ease,
      transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);

    .nav-index {
      display: block;
      color: ${$active ? '#171d27' : '#7d899b'};
      font-size: 9px;
      font-weight: 820;
      letter-spacing: 0.06em;
      transition: color 0.24s ease;
    }

    .dot {
      position: absolute;
      bottom: 7px;
      width: ${$active ? '15px' : '5px'};
      height: 2px;
      border: 0;
      border-radius: 999px;
      background: ${$active ? '#568fc8' : '#566275'};
      box-shadow: none;
      transform: none;
      transition:
        width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
        background-color 0.24s ease;
    }

    .label {
      right: calc(100% + 0.8rem);
      padding: 0.65rem 0.9rem 0.6rem;
      border: 1px solid rgba(224, 232, 244, 0.11);
      border-radius: 12px;
      background: rgba(12, 16, 25, 0.94);
      color: #f6f8fb;
      font-size: 0.76rem;
      box-shadow: 0 14px 34px rgba(0, 0, 0, 0.34);
    }

    &:hover,
    &:focus-visible {
      background: ${$active ? 'rgba(239, 244, 251, 0.96)' : 'rgba(237, 243, 251, 0.08)'};
      outline: none;
      transform: translateX(-2px);
    }

    &:hover .label,
    &:focus-visible .label {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }

    &:hover .dot,
    &:focus-visible .dot {
      width: 15px;
      border: 0;
      background: ${$active ? '#568fc8' : '#8395aa'};
      box-shadow: none;
      transform: none;
    }

    @media (max-width: 620px) {
      width: 34px;
      height: 36px;
      border-radius: 10px;
      background: ${$active ? 'rgba(239, 244, 251, 0.93)' : 'transparent'};
      box-shadow: ${$active ? '0 7px 16px rgba(0, 0, 0, 0.25)' : 'none'};

      .label {
        display: none;
      }

      .nav-index {
        display: block;
        opacity: ${$active ? '1' : '0'};
        color: #171d27;
        font-size: 7px;
        transform: translateY(-2px);
        transition: opacity 0.2s ease;
      }

      .dot {
        bottom: 7px;
        width: ${$active ? '12px' : '4px'};
        height: 2px;
        background: ${$active ? '#568fc8' : '#687488'};
      }

      &:hover,
      &:focus-visible {
        transform: none;
      }

      &:hover .dot,
      &:focus-visible .dot {
        width: ${$active ? '12px' : '8px'};
        background: ${$active ? '#568fc8' : '#8b99ab'};
      }
    }

    @media (max-height: 680px) and (min-width: 621px) {
      height: 34px;
    }

    @media (max-width: 620px) and (max-height: 620px) {
      height: 30px;
    }
  `}

  @media (max-height: 680px) and (min-width: 621px) {
    ${({ $variant }) => $variant === 'light' && css`
      width: 38px;
      height: 38px;

      .dot {
        bottom: 6px;
      }
    `}
  }
`;

const Header = ({ variant = 'dark' }) => {
  const location = useLocation();
  const isProductPage = location.pathname === '/product';
  const isEducationPage = location.pathname === '/education';
  const isShowcasePage = location.pathname === '/showcase';
  const isHomepage = location.pathname === '/';
  const headerVariant = variant === 'light' ? 'light' : 'dark';

  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [tempShowLabel, setTempShowLabel] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const labelTimerRef = useRef(null); // 타이머 관리를 위한 Ref 추가

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const SERVICE_URL = "https://narnia-lab.com/server/";
  const [showDevServiceLink, setShowDevServiceLink] = useState(false);
  const [isCompactNavigation, setIsCompactNavigation] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 991.98,
  );
  const [isPortLauncherOpen, setIsPortLauncherOpen] = useState(false);
  const [devPort, setDevPort] = useState('');
  const [devPortError, setDevPortError] = useState('');
  const devServiceHideTimerRef = useRef(null);
  const touchMouseSuppressionTimerRef = useRef(null);
  const suppressMouseRevealRef = useRef(false);
  const serviceAccessRef = useRef(null);
  const devServiceActionsRef = useRef(null);
  const portLauncherRef = useRef(null);
  const portInputRef = useRef(null);

  // 도입 문의 상태
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    organization_type: 'company',
    company_name: '',
    contact_name: '',
    contact_role: '',
    phone_number: '',
    email: '',
    estimated_users: '',
    content: '',
  });

  const handleInquiryChange = (e) => {
    setInquiryForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setInquiryLoading(true);
    try {
      const res = await fetch('/my-page/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inquiryForm),
      });
      if (res.ok) {
        setInquirySubmitted(true);
      } else {
        alert('문의 접수에 실패했습니다. 다시 시도해주세요.');
      }
    } catch {
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setInquiryLoading(false);
    }
  };

  const closeInquiryModal = () => {
    setIsInquiryOpen(false);
    setInquirySubmitted(false);
    setInquiryForm({
      organization_type: 'company',
      company_name: '',
      contact_name: '',
      contact_role: '',
      phone_number: '',
      email: '',
      estimated_users: '',
      content: '',
    });
  };

  const navItems = useMemo(() => {
    if (isProductPage) {
      return [
        {
          id: 'product-overview',
          label: '제품 개요',
          subLabel: 'Product Overview',
          href: '#product-overview',
        },
        {
          id: 'product-flow',
          label: '사용 흐름',
          subLabel: 'How It Works',
          href: '#product-flow',
        },
        {
          id: 'product-film',
          label: '제품 영상',
          subLabel: 'Product Film',
          href: '#product-film',
        },
        {
          id: 'product-capabilities',
          label: '핵심 기능',
          subLabel: 'Core Experience',
          href: '#product-capabilities',
        },
        {
          id: 'product-interface',
          label: '실제 화면',
          subLabel: 'Real Workspace',
          href: '#product-interface',
        },
        {
          id: 'product-use-cases',
          label: '활용 사례',
          subLabel: 'Made With Narnia',
          href: '#product-use-cases',
        },
        {
          id: 'product-start',
          label: '시작하기',
          subLabel: 'Ready to Create',
          href: '#product-start',
        },
      ];
    }

    if (isEducationPage) {
      return [
        {
          id: 'education-outcomes',
          label: '교육의 변화',
          subLabel: 'Learning Outcomes',
          href: '#education-outcomes',
        },
        {
          id: 'education-architect',
          label: '교육 철학',
          subLabel: 'Coder to Architect',
          href: '#education-architect',
        },
        {
          id: 'education-system',
          label: '교육 시스템',
          subLabel: 'Narnia System',
          href: '#education-system',
        },
        {
          id: 'education-roadmap',
          label: '성장 로드맵',
          subLabel: 'Growth Roadmap',
          href: '#education-roadmap',
        },
        {
          id: 'education-sprint',
          label: '학습 방식',
          subLabel: 'Learning Method',
          href: '#education-sprint',
        },
        {
          id: 'education-stage',
          label: '발표 무대',
          subLabel: 'Beyond the Class',
          href: '#education-stage',
        },
        {
          id: 'education-start',
          label: '시작 안내',
          subLabel: 'Ready to Start',
          href: '#education-start',
        },
      ];
    }

    if (isShowcasePage) {
      return [
        {
          id: 'showcase-top',
          label: '작품 전시관',
          subLabel: 'Showcase',
          href: '#showcase-top',
        },
        {
          id: 'showcase-all',
          label: '전체 작품',
          subLabel: 'All Projects',
          href: '#showcase-all',
        },
      ];
    }

    const philosophy = {
      id: 'philosophy',
      label: '교육 철학',
      subLabel: 'Our Philosophy',
      href: '#philosophy',
    };
    const showcase = {
      id: 'projects',
      label: '작품 전시관',
      subLabel: 'Live Showcase',
      href: '/showcase',
    };
    const technology = {
      id: 'about',
      label: '핵심 기술',
      subLabel: 'Core Technology',
      href: '#about',
    };
    const vision = {
      id: 'vision',
      label: '나니아 비전',
      subLabel: 'Narnia Vision',
      href: '#vision',
    };
    const curriculum = SHOW_CURRICULUM_SECTION
      ? {
        id: 'camps',
        label: '교육 과정',
        subLabel: 'Elite Curriculum',
        href: '#camps',
      }
      : null;

    return (
      isHomepage
        ? [philosophy, technology, showcase, vision]
        : [philosophy, showcase, technology, vision, curriculum]
    ).filter(Boolean);
  }, [isEducationPage, isHomepage, isProductPage, isShowcasePage]);

  // 레이블 표시 제어 함수 (3초 후 자동 해제)
  const showLabelTemporarily = (id) => {
    setTempShowLabel(id);
    if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    labelTimerRef.current = setTimeout(() => {
      setTempShowLabel(null);
      labelTimerRef.current = null;
    }, 3000);
  };

  const hideTemporaryLabel = () => {
    if (labelTimerRef.current) {
      clearTimeout(labelTimerRef.current);
      labelTimerRef.current = null;
    }
    setTempShowLabel(null);
  };

  const clearDevServiceHideTimer = () => {
    if (devServiceHideTimerRef.current) {
      clearTimeout(devServiceHideTimerRef.current);
      devServiceHideTimerRef.current = null;
    }
  };

  const startDevServiceReveal = () => {
    if (showDevServiceLink) return;
    setShowDevServiceLink(true);
  };

  const startNonTouchDevServiceReveal = () => {
    if (suppressMouseRevealRef.current) return;
    startDevServiceReveal();
  };

  const handleServiceAreaEnter = () => {
    clearDevServiceHideTimer();
    startNonTouchDevServiceReveal();
  };

  const suppressTouchGeneratedMouseReveal = () => {
    if (touchMouseSuppressionTimerRef.current) {
      clearTimeout(touchMouseSuppressionTimerRef.current);
      touchMouseSuppressionTimerRef.current = null;
    }
    suppressMouseRevealRef.current = true;
  };

  const releaseTouchGeneratedMouseReveal = () => {
    if (touchMouseSuppressionTimerRef.current) {
      clearTimeout(touchMouseSuppressionTimerRef.current);
    }
    touchMouseSuppressionTimerRef.current = setTimeout(() => {
      suppressMouseRevealRef.current = false;
      touchMouseSuppressionTimerRef.current = null;
    }, TOUCH_MOUSE_SUPPRESSION_MS);
  };

  const hideDevServiceReveal = () => {
    clearDevServiceHideTimer();
    setShowDevServiceLink(false);
    setIsPortLauncherOpen(false);
    setDevPortError('');
  };

  const scheduleDevServiceHide = () => {
    clearDevServiceHideTimer();
    if (!showDevServiceLink) return;
    if (serviceAccessRef.current?.contains(document.activeElement)) return;

    devServiceHideTimerRef.current = setTimeout(() => {
      devServiceHideTimerRef.current = null;
      hideDevServiceReveal();
    }, DESKTOP_DEV_HIDE_DELAY_MS);
  };

  const handleServiceAreaBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      hideDevServiceReveal();
    }
  };

  const handleServiceAreaKeyDown = (event) => {
    if (event.key !== 'Escape' || !showDevServiceLink) return;
    event.preventDefault();
    event.stopPropagation();
    hideDevServiceReveal();
  };

  const handleServiceLinkClick = () => {
    hideDevServiceReveal();
    handleNavLinkClick();
  };

  const handleDevPortChange = (event) => {
    setDevPort(event.target.value.replace(/\D/g, '').slice(0, 5));
    if (devPortError) setDevPortError('');
  };

  const handleDevPortSubmit = (event) => {
    event.preventDefault();
    const parsedPort = Number(devPort);

    if (!/^\d{5}$/.test(devPort) || parsedPort < 10000 || parsedPort > 19999) {
      setDevPortError('10000–19999 사이의 포트 번호를 입력해 주세요.');
      return;
    }

    setDevPortError('');
    const openedWindow = window.open(
      `https://narnialab.duckdns.org:${parsedPort}`,
      '_blank',
      'noopener,noreferrer',
    );
    if (openedWindow) openedWindow.opener = null;
  };

  const togglePortLauncher = () => {
    setDevPortError('');
    setIsPortLauncherOpen((isOpen) => !isOpen);
  };

  const closePortLauncher = () => {
    setDevPortError('');
    setIsPortLauncherOpen(false);
  };

  const handlePortAreaBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      closePortLauncher();
    }
  };

  // 섹션 변경 시 레이블 일시 노출 로직 (모바일/데스크탑 공통)
  useEffect(() => {
    if (
      headerVariant !== 'light'
      && !isProductPage
      && activeSection
      && activeSection !== 'home'
    ) {
      showLabelTemporarily(activeSection);
    }
  }, [activeSection, headerVariant, isProductPage]);

  useEffect(() => {
    return () => {
      if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      clearDevServiceHideTimer();
      if (touchMouseSuppressionTimerRef.current) {
        clearTimeout(touchMouseSuppressionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const updateCompactNavigation = () => {
      setIsCompactNavigation(window.innerWidth <= 991.98);
    };

    updateCompactNavigation();
    window.addEventListener('resize', updateCompactNavigation);
    return () => window.removeEventListener('resize', updateCompactNavigation);
  }, []);

  useEffect(() => {
    if (!showDevServiceLink) return undefined;

    const handleDocumentKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      clearDevServiceHideTimer();
      setShowDevServiceLink(false);
      setIsPortLauncherOpen(false);
      setDevPortError('');
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [showDevServiceLink]);

  useEffect(() => {
    if (
      !showDevServiceLink
      || (
        typeof window.matchMedia === 'function'
        && !window.matchMedia('(max-width: 991.98px)').matches
      )
    ) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame?.(() => {
      devServiceActionsRef.current?.scrollIntoView?.({
        block: 'end',
        inline: 'nearest',
        behavior: 'auto',
      });
    });

    return () => {
      if (frameId !== undefined) window.cancelAnimationFrame?.(frameId);
    };
  }, [showDevServiceLink]);

  useEffect(() => {
    if (!isPortLauncherOpen || isCompactNavigation) return undefined;

    const revealPortLauncher = () => {
      portLauncherRef.current?.scrollIntoView?.({
        block: 'end',
        inline: 'nearest',
        behavior: 'smooth',
      });
    };

    const frameId = window.requestAnimationFrame?.(revealPortLauncher);

    return () => {
      if (frameId !== undefined) window.cancelAnimationFrame?.(frameId);
    };
  }, [isCompactNavigation, isPortLauncherOpen]);

  useEffect(() => {
    let animationFrameId = null;

    const updatePagePosition = () => {
      animationFrameId = null;
      setScrolled(window.scrollY > 50);

      const headerHeight = document
        .querySelector('nav[aria-label="주요 메뉴"]')
        ?.getBoundingClientRect().height ?? 0;
      const visibleViewportHeight = Math.max(0, window.innerHeight - headerHeight);
      const activationOffset = headerVariant === 'light'
        ? headerHeight + (visibleViewportHeight * 0.38)
        : Math.min(window.innerHeight * 0.34, 280);
      const scrollPos = window.scrollY + activationOffset;
      const sections = navItems
        .map((item) => {
          const element = document.getElementById(item.id);
          if (!element) return null;

          return {
            id: item.id,
            top: element.getBoundingClientRect().top + window.scrollY,
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.top - right.top);

      const currentSection = sections.reduce(
        (current, section) => (scrollPos >= section.top ? section.id : current),
        'home',
      );
      setActiveSection(currentSection);
    };

    const schedulePagePositionUpdate = () => {
      if (animationFrameId !== null) return;
      if (typeof window.requestAnimationFrame !== 'function') {
        updatePagePosition();
        return;
      }
      animationFrameId = window.requestAnimationFrame(updatePagePosition);
    };

    window.addEventListener('scroll', schedulePagePositionUpdate, { passive: true });
    window.addEventListener('resize', schedulePagePositionUpdate);
    window.addEventListener('hashchange', schedulePagePositionUpdate);

    const layoutObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(schedulePagePositionUpdate)
      : null;
    if (layoutObserver && document.body) {
      layoutObserver.observe(document.body);
    }

    schedulePagePositionUpdate();
    
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      setUser(storedUser);
      fetchUserInfo(token);
    }

    return () => {
      window.removeEventListener('scroll', schedulePagePositionUpdate);
      window.removeEventListener('resize', schedulePagePositionUpdate);
      window.removeEventListener('hashchange', schedulePagePositionUpdate);
      layoutObserver?.disconnect();
      if (animationFrameId !== null) {
        window.cancelAnimationFrame?.(animationFrameId);
      }
    };
  }, [headerVariant, navItems]);

  const handleNavLinkClick = () => {
    setExpanded(false);
  };

  const fetchUserInfo = async (token) => {
    try {
      const response = await fetch('/api/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      }
    } catch (error) {
      console.error("Failed to fetch user info error:", error);
    }
  };

  const handleLogin = (username, token) => {
    localStorage.setItem('user', username);
    localStorage.setItem('token', token);
    setUser(username);
    fetchUserInfo(token);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setUserInfo(null);
  };

  return (
    <>
      <StyledNavbar 
        $scrolled={scrolled}
        $variant={headerVariant}
        $productPage={isProductPage}
        $menuOpen={expanded}
        data-theme={headerVariant}
        aria-label="주요 메뉴"
        expand="lg" 
        fixed="top"
        expanded={expanded}
        onToggle={(val) => setExpanded(val)}
      >
        <Container>
          <Navbar.Brand as={Link} to="/" onClick={() => setExpanded(false)}>
            <img src="/naniaraeb_white.png" alt="NARNIA LAB" />
          </Navbar.Brand>
          <StyledToggle
            $variant={headerVariant}
            $productPage={isProductPage}
            aria-controls="basic-navbar-nav"
            label={expanded ? '메뉴 닫기' : '메뉴 열기'}
          />
          <Navbar.Collapse id="basic-navbar-nav">
            <CloseButton
              $variant={headerVariant}
              $productPage={isProductPage}
              type="button"
              aria-label="메뉴 닫기"
              onClick={() => setExpanded(false)}
            >
              ×
            </CloseButton>
            <Nav className="ms-auto align-items-center">
              <div className="d-lg-none w-100 text-center">
                {navItems.map((item) => (
                  <Nav.Link
                    key={item.id}
                    href={item.href}
                    onClick={handleNavLinkClick}
                    data-label={item.subLabel}
                    aria-current={activeSection === item.id ? 'location' : undefined}
                    style={headerVariant === 'light' ? undefined : {
                      color: activeSection === item.id ? 'var(--accent-color-1)' : '#fff',
                      opacity: activeSection === item.id ? 1 : 0.7
                    }}
                  >
                    {item.label}
                  </Nav.Link>
                ))}
              </div>

              <div className="mobile-btn-row">
                <Nav.Link
                  as={Link}
                  to="/education"
                  className="terminal-access-btn education-btn"
                  data-header-action="education"
                  aria-current={location.pathname === '/education' ? 'page' : undefined}
                  onClick={handleNavLinkClick}
                  style={headerVariant === 'light' ? undefined : {
                    borderColor: 'rgba(96,165,250,0.4)',
                    background: 'rgba(96,165,250,0.06)',
                    color: 'rgba(186,220,255,1)'
                  }}
                >
                  <FiBookOpen size={15} />
                  <span>교육소개</span>
                </Nav.Link>

                <Nav.Link
                  as={Link}
                  to="/product"
                  className="terminal-access-btn product-btn"
                  data-header-action="product"
                  aria-current={location.pathname === '/product' ? 'page' : undefined}
                  onClick={handleNavLinkClick}
                  style={headerVariant === 'light' ? undefined : {
                    borderColor: 'rgba(167,139,250,0.4)',
                    background: 'rgba(167,139,250,0.06)',
                    color: 'rgba(200,180,255,1)'
                  }}
                >
                  <FiLayers size={15} />
                  <span>제품소개</span>
                </Nav.Link>

                <Nav.Link
                  as="button"
                  className="terminal-access-btn inquiry-btn"
                  data-header-action="inquiry"
                  onClick={() => { handleNavLinkClick(); setIsInquiryOpen(true); }}
                >
                  <FiMail size={16} />
                  <span>도입 문의</span>
                </Nav.Link>

                <div
                  className="service-access-wrap"
                  ref={serviceAccessRef}
                  onMouseEnter={handleServiceAreaEnter}
                  onMouseLeave={scheduleDevServiceHide}
                  onTouchStart={suppressTouchGeneratedMouseReveal}
                  onTouchEnd={releaseTouchGeneratedMouseReveal}
                  onTouchCancel={releaseTouchGeneratedMouseReveal}
                  onFocus={handleServiceAreaEnter}
                  onBlur={handleServiceAreaBlur}
                  onKeyDown={handleServiceAreaKeyDown}
                >
                  <div className="service-primary-row">
                    <Nav.Link
                      href={SERVICE_URL}
                      target="_blank"
                      className="terminal-access-btn service-btn"
                      data-header-action="service"
                      rel="noopener noreferrer"
                      onClick={handleServiceLinkClick}
                    >
                      <FiExternalLink size={16} />
                      <span>서비스 바로가기</span>
                    </Nav.Link>
                  </div>

                  {(isCompactNavigation || showDevServiceLink) && (
                    <div
                      className="dev-service-actions"
                      id="dev-service-actions"
                      ref={devServiceActionsRef}
                      onMouseEnter={clearDevServiceHideTimer}
                    >
                      <div className="port-access-wrap" onBlur={handlePortAreaBlur}>
                        <button
                          type="button"
                          className="terminal-access-btn port-access-trigger"
                          aria-label="포트 연결"
                          aria-controls="dev-port-launcher"
                          aria-expanded={isPortLauncherOpen}
                          onClick={togglePortLauncher}
                        >
                          <FiTerminal size={15} aria-hidden="true" />
                          <span>포트 연결</span>
                        </button>

                        {isPortLauncherOpen && (
                          <form
                            id="dev-port-launcher"
                            className="dev-port-launcher"
                            ref={portLauncherRef}
                            aria-label="포트 번호로 서버 열기"
                            onSubmit={handleDevPortSubmit}
                          >
                            <div className="dev-port-launcher-heading">
                              <span>포트로 바로 연결</span>
                              <div className="dev-port-heading-actions">
                                <span className="dev-port-kicker">DEV PORT</span>
                                <button
                                  type="button"
                                  className="dev-port-close"
                                  aria-label="포트 입력 닫기"
                                  onClick={closePortLauncher}
                                >
                                  <FiX size={14} aria-hidden="true" />
                                </button>
                              </div>
                            </div>
                            <div className="dev-port-launcher-row">
                              <label className="dev-port-input-shell">
                                <span className="dev-port-host" aria-hidden="true">
                                  narnialab.duckdns.org:
                                </span>
                                <input
                                  className="dev-port-input"
                                  ref={portInputRef}
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  enterKeyHint="go"
                                  autoComplete="off"
                                  autoFocus={isCompactNavigation}
                                  maxLength={5}
                                  value={devPort}
                                  placeholder="19072"
                                  onChange={handleDevPortChange}
                                  onFocus={(event) => {
                                    if (event.currentTarget.value) {
                                      event.currentTarget.select();
                                    }
                                  }}
                                  aria-label="개발 서버 포트 번호"
                                />
                              </label>
                              <button type="submit" className="dev-port-submit">
                                <span>접속</span>
                                <FiExternalLink size={13} aria-hidden="true" />
                              </button>
                            </div>
                            {devPortError && (
                              <p className="dev-port-error" role="alert">
                                {devPortError}
                              </p>
                            )}
                          </form>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {user ? (
                <Dropdown align="end" className="header-account mt-3 mt-lg-0">
                  <Dropdown.Toggle as={Nav.Link} id="dropdown-custom-components" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaUserCircle size={20} />
                    <span>{user}님</span>
                    {userInfo && (
                      <span className="d-day-badge" style={{ 
                        fontSize: '0.65rem', 
                        background: userInfo.role === 'special_member' ? '#6c5ce7' : 
                                   (userInfo.is_active ? (userInfo.role === 'tester' ? '#00a8ff' : '#4cd137') : '#e84118'), 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: '20px',
                        fontWeight: '800',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                        marginLeft: '5px',
                        textTransform: 'uppercase'
                      }}>
                        {userInfo.role === 'special_member' ? 'MEMBER' : 
                         (userInfo.is_active ? (userInfo.role === 'tester' ? `TEST D-${userInfo.days_remaining}` : `D-${userInfo.days_remaining}`) : '만료됨')}
                      </span>
                    )}
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="header-account-menu" style={{ background: 'rgba(20, 20, 30, 0.95)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {userInfo && userInfo.debug && (
                       <div style={{ padding: '8px 16px', color: '#ffeb3b', fontSize: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '5px', whiteSpace: 'pre-wrap' }}>
                         DEBUG: {JSON.stringify(userInfo.debug, null, 2)}
                       </div>
                    )}
                    {userInfo && userInfo.expiry_date && (
                       <div style={{ padding: '8px 16px', color: '#ccc', fontSize: '0.85rem', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '5px' }}>
                         만료일: {userInfo.expiry_date}
                       </div>
                    )}
                    <Dropdown.Item as={Link} to="/mypage" onClick={handleNavLinkClick} style={{ color: 'white' }}>마이페이지</Dropdown.Item>
                    <Dropdown.Item onClick={() => { handleLogout(); handleNavLinkClick(); }} style={{ color: 'white' }}>로그아웃</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : null}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </StyledNavbar>

      <FloatingNav
        data-theme={headerVariant}
        data-page={isProductPage
          ? 'product'
          : (isEducationPage ? 'education' : (isShowcasePage ? 'showcase' : undefined))}
        aria-label="페이지 섹션"
      >
        {navItems.map((item, index) => (
          <NavItem
            key={item.id}
            $variant={headerVariant}
            $productPage={isProductPage}
            $active={activeSection === item.id}
            href={item.href}
            aria-label={item.label}
            aria-current={activeSection === item.id ? 'location' : undefined}
            onMouseEnter={() => {
              if (headerVariant !== 'light' && window.innerWidth > 991.98) {
                showLabelTemporarily(item.id);
              }
            }}
            onMouseLeave={hideTemporaryLabel}
            onBlur={hideTemporaryLabel}
            onClick={() => {
              if (headerVariant !== 'light' && window.innerWidth > 991.98) {
                showLabelTemporarily(item.id);
              }
            }}
          >
            <span
              className={`label ${
                headerVariant !== 'light' && tempShowLabel === item.id ? 'show-temp' : ''
              }`}
            >
              {item.label}
            </span>
            <span className="nav-index" aria-hidden="true">
              {String(index + 1).padStart(2, '0')}
            </span>
            <div className="dot" />
          </NavItem>
        ))}
      </FloatingNav>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
      />

      {isInquiryOpen && (
        <InquiryOverlay $variant={headerVariant}>
          <InquiryModal
            $variant={headerVariant}
            data-theme={headerVariant}
            role="dialog"
            aria-modal="true"
            aria-labelledby="inquiry-title"
          >
            {inquirySubmitted ? (
              <div className="success-message">
                <div className="icon"><FiCheck /></div>
                <h3>문의가 접수되었습니다!</h3>
                <p>빠른 시간 내에 담당자가 연락드리겠습니다.</p>
                <div className="btn-row" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
                  <button className="btn-submit" style={{ flex: 'none', padding: '0.8rem 2.5rem' }} onClick={closeInquiryModal}>확인</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleInquirySubmit}>
                <div className="inquiry-heading-row">
                  <div>
                    <span className="inquiry-kicker">NARNIA FOR ORGANIZATIONS</span>
                    <h2 id="inquiry-title">서비스 도입 문의</h2>
                  </div>
                  <button
                    type="button"
                    className="inquiry-close"
                    aria-label="도입 문의 닫기"
                    onClick={closeInquiryModal}
                  >
                    <FiX size={20} aria-hidden="true" />
                  </button>
                </div>
                <p className="subtitle">학교·기업·학원 등 단체를 위한 나니아랩 서비스 도입을 문의해주세요.</p>
                <div className="inquiry-benefits" aria-label="도입 문의 지원 내용">
                  <span>교육 환경 맞춤 구성</span>
                  <span>예상 인원별 안내</span>
                  <span>담당자 직접 상담</span>
                </div>

                <label htmlFor="inquiry-organization-type">기관 유형 <span className="required">*</span></label>
                <select id="inquiry-organization-type" name="organization_type" value={inquiryForm.organization_type} onChange={handleInquiryChange} required>
                  <option value="company">기업</option>
                  <option value="school">학교</option>
                  <option value="academy">학원</option>
                  <option value="other">기타</option>
                </select>

                <label htmlFor="inquiry-company-name">기관명 <span className="required">*</span></label>
                <input id="inquiry-company-name" name="company_name" value={inquiryForm.company_name} onChange={handleInquiryChange} placeholder="예: ○○중학교, ○○기업" required />

                <div className="inquiry-field-grid">
                  <div>
                    <label htmlFor="inquiry-contact-name">담당자명 <span className="required">*</span></label>
                    <input id="inquiry-contact-name" name="contact_name" value={inquiryForm.contact_name} onChange={handleInquiryChange} placeholder="홍길동" required />
                  </div>
                  <div>
                    <label htmlFor="inquiry-contact-role">직책/직위</label>
                    <input id="inquiry-contact-role" name="contact_role" value={inquiryForm.contact_role} onChange={handleInquiryChange} placeholder="예: 팀장, 교사" />
                  </div>
                </div>

                <div className="inquiry-field-grid">
                  <div>
                    <label htmlFor="inquiry-phone-number">연락처 <span className="required">*</span></label>
                    <input id="inquiry-phone-number" name="phone_number" value={inquiryForm.phone_number} onChange={handleInquiryChange} placeholder="010-0000-0000" required />
                  </div>
                  <div>
                    <label htmlFor="inquiry-email">이메일 <span className="required">*</span></label>
                    <input id="inquiry-email" name="email" type="email" value={inquiryForm.email} onChange={handleInquiryChange} placeholder="example@email.com" required />
                  </div>
                </div>

                <label htmlFor="inquiry-estimated-users">예상 사용 인원</label>
                <select id="inquiry-estimated-users" name="estimated_users" value={inquiryForm.estimated_users} onChange={handleInquiryChange}>
                  <option value="">선택해주세요</option>
                  <option value="1~10명">1~10명</option>
                  <option value="11~30명">11~30명</option>
                  <option value="31~50명">31~50명</option>
                  <option value="51~100명">51~100명</option>
                  <option value="100명 이상">100명 이상</option>
                </select>

                <label htmlFor="inquiry-content">문의 내용 <span className="required">*</span></label>
                <textarea id="inquiry-content" name="content" value={inquiryForm.content} onChange={handleInquiryChange} placeholder="도입 목적, 희망 일정, 궁금한 사항 등을 자유롭게 작성해주세요." required />

                <div className="btn-row">
                  <button type="button" className="btn-cancel" onClick={closeInquiryModal}>취소</button>
                  <button type="submit" className="btn-submit" disabled={inquiryLoading}>
                    {inquiryLoading ? '접수 중...' : '문의 접수하기'}
                  </button>
                </div>
              </form>
            )}
          </InquiryModal>
        </InquiryOverlay>
      )}
    </>
  );
};

export default Header;
